/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { action } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Typography from '@material-ui/core/Typography';

import WidgetStore from 'stores/WidgetStore';
import UserStore from 'stores/UserStore';
import type { WidgetItem } from 'stores/DashboardStore';
import { Droppable } from 'components/DragDrop';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import AssetAssignment from '../AssetAssignment/AssetAssignment';
import AssetsByLocation from '../AssetsByLocation/AssetsByLocation';
import AssetsByModel from '../AssetsByModel/AssetsByModel';
import AssetsByProject from '../AssetsByProject/AssetsByProject';
import AssetsByTypeBar from '../AssetsByTypeBar/AssetsByTypeBar';
import AssetsByUser from '../AssetsByUser/AssetsByUser';
import AssetsByUserType from '../AssetsByUserType/AssetsByUserType';
import AssetQuality from '../AssetQuality/AssetQuality';
import AssetStatus from '../AssetStatus/AssetStatus';
import Calibrations from '../Calibrations/Calibrations';
import Contracts from '../Contracts/Contracts';
import CostCenters from '../CostCenters/CostCenters';
import Projects from '../Projects/Projects';
import TradingAccounts from '../TradingAccounts/TradingAccounts';
import FullScreenViewer from '../../components/FullScreenViewer';
import availableWidgets from '../AvailableContainer/availableWidgetList';

import styles from './MainWidgetContainer.module.scss';
import UserWidgetContainer from '../UserWidgetContainer/UserWidgetContainer';

type InjectedProps = {
  widgetStore: WidgetStore,
  userStore: UserStore,
} & TranslatorProps;

@translate()
@inject('widgetStore', 'userStore')
@observer
export default class MainWidgetContainer extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  static supportedWidgets = ['empty'].concat(
    availableWidgets.map(availableWidget => availableWidget.data)
  );

  async componentDidMount() {
    const {
      widgetStore,
      userStore: {
        activeUser: { widget_preferences },
      },
    } = this.props;

    // remove old user config, and add new 4 widgets
    if (widget_preferences.length < 4) {
      await widgetStore.updateWidgets(widgetStore.defaultWidgets);
    }
  }

  @action
  addWidget = (widgetPosition: number, widget: string) => {
    const { widgets } = this.props.widgetStore;
    const existingWidgets = widgets.filter(widget => widget.position !== widgetPosition);
    this.props.widgetStore.widgets = [
      ...existingWidgets.map(widget => ({
        ...widget,
      })),
      {
        position: widgetPosition,
        variant: 'default',
        widget: widget,
      },
    ];
  };

  onDropWidget = (widgetPosition: number) => ({ widget: newWidget }: { widget: string }) => {
    this.addWidget(widgetPosition, newWidget);
  };

  emptyWidget = (widgetPosition: number) => () => {
    this.addWidget(widgetPosition, 'empty');

    const { widgetStore } = this.props;
    if (
      widgetStore.widgets.length > 1 &&
      widgetStore.widgets.every(widget => widget.widget === 'empty')
    ) {
      widgetStore.widgets = [
        {
          position: 1,
          variant: 'default',
          widget: 'empty',
        },
      ];
    }
  };

  @action
  expandWidget = (widgetPosition: number) => () => {
    const { widgetStore } = this.props;
    const existingWidget = widgetStore.widgets.find(widget => widget.position === widgetPosition);

    if (existingWidget) {
      widgetStore.widgets = [
        {
          ...existingWidget,
          position: 1,
        },
      ];
    }
  };

  getWidgetComponent = (widgetItem: WidgetItem, isFullScreen?: boolean, forceSize?: string) => {
    const size = forceSize || 'canvas-50';

    switch (widgetItem.widget) {
      case 'AssetAssignment':
        return <AssetAssignment size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'AssetsByLocation':
        return <AssetsByLocation size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'AssetsByProject':
        return <AssetsByProject size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'AssetsByModel':
        return <AssetsByModel size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'AssetsByTypeBar':
        return <AssetsByTypeBar size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'AssetsByUser':
        return <AssetsByUser size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'AssetQuality':
        return <AssetQuality size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'AssetStatus':
        return <AssetStatus size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'AssetsByUserType':
        return <AssetsByUserType size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'Calibrations':
        return <Calibrations size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'Contracts':
        return <Contracts size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'CostCenters':
        return <CostCenters size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'Projects':
        return <Projects size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      case 'TradingAccounts':
        return <TradingAccounts size={size} widgetItem={widgetItem} isFullScreen={isFullScreen} />;
      default:
        return null;
    }
  };

  render() {
    const { t, widgetStore } = this.props;

    return (
      <React.Fragment>
        <FullScreenViewer widgetRenderer={this.getWidgetComponent} />

        <div className={styles['main-widget-container']}>
          {widgetStore.widgets.map(item => {
            return item.widget === 'empty' ? (
              <Droppable
                key={item.position}
                types={['widget']}
                onDrop={this.onDropWidget(item.position)}
                className={`${styles['widget-area']}`}
              >
                <div className={styles['widget-area-border']} />
                <div className={styles['widget-dropper-text']}>
                  <svg>
                    <use xlinkHref="#icon-download" />
                  </svg>
                  <div>
                    {t('general:dashboards.messages.no_selected_widget')}
                    <small>{t('general:dashboards.messages.drop_widget')}</small>
                  </div>
                </div>
              </Droppable>
            ) : (
              <div className={`${styles['widget-area']} ${styles['widget-left']}`}>
                {this.getWidgetComponent(item)}
              </div>
            );
          })}
        </div>
      </React.Fragment>
    );
  }
}
